import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { FILE_FORMATS_QUERY } from '../../graphql/queries';
import { ADMIN_UPDATE_FILE_FORMATS_MUTATION } from '../../graphql/mutations';
import {
  DOCUMENT_VIEWERS, documentFormatsOf, documentFormatsProblem, normalizeExtension, type DocumentViewer,
} from '../../utils/fileFormats';
import styles from './FileFormatsSettings.module.css';

interface Row { key: number; extension: string; viewer: DocumentViewer }

const FileFormatsSettings = () => {
  const { data, error: loadError } = useQuery(FILE_FORMATS_QUERY, { fetchPolicy: 'cache-and-network' });
  const ready = !!data?.fileFormats;
  const nextKey = useRef(0);
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const toRows = (documents: { extension: string; viewer: string }[]) =>
    documentFormatsOf(documents).map((f) => ({ key: nextKey.current++, extension: f.extension, viewer: f.viewer }));

  useEffect(() => {
    if (data?.fileFormats) setRows(toRows(data.fileFormats.documents));
  }, [data]);

  const [save, { loading: saving }] = useMutation(ADMIN_UPDATE_FILE_FORMATS_MUTATION, {
    update: (cache, { data: result }) => {
      if (result?.adminUpdateFileFormats) cache.writeQuery({ query: FILE_FORMATS_QUERY, data: { fileFormats: result.adminUpdateFileFormats } });
    },
    onCompleted: (result) => {
      setRows(toRows(result.adminUpdateFileFormats.documents));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) => setError(err.message),
  });

  const edit = (key: number, patch: Partial<Row>) => {
    setError('');
    setRows((list) => list.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const add = () => {
    setError('');
    setRows((list) => [...list, { key: nextKey.current++, extension: '', viewer: 'download' }]);
  };
  const remove = (key: number) => {
    setError('');
    setRows((list) => list.filter((r) => r.key !== key));
  };
  const handleSave = () => {
    const problem = documentFormatsProblem(rows);
    setError(problem);
    if (problem) return;
    save({ variables: { input: { documents: rows.map((r) => ({ extension: normalizeExtension(r.extension), viewer: r.viewer })) } } });
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className="material-symbols-outlined text-[#0061ff]">draft</span>
        <h3 className={styles.title}>File Formats</h3>
      </div>

      <p className={styles.description}>
        Which file types the project sections accept, and what each one opens in. Uploads of any other
        type are refused. Files already uploaded keep opening as before when their format is taken off
        a list.
      </p>

      <div className={styles.section}>
        <div>
          <h4 className={styles.sectionTitle}>Documents</h4>
          <p className={styles.description}>
            <strong>PDF Viewer</strong> shows the file in the browser&apos;s own viewer, which suits PDFs
            and plain text. <strong>Image Viewer</strong> shows photos and pictures with zoom and pan:
            .png, .jpg, .jpeg, .gif, .webp, .bmp, .svg, .avif, and .tif / .tiff, which it decodes itself
            since most browsers cannot. <strong>Download only</strong> lists the file with a Download
            button. Drawings, models, scans and other formats with a section of their own cannot be
            added here.
          </p>
        </div>

        <div className={styles.table} role="table" aria-label="Document formats">
          <div className={styles.headRow} role="row">
            <span role="columnheader">Extension</span>
            <span role="columnheader">Opens in</span>
            <span />
          </div>
          {rows.map((row, i) => (
            <div key={row.key} className={styles.row} role="row">
              <div className={styles.extensionField}>
                <span className={styles.dot} aria-hidden>.</span>
                <input
                  className="input-field"
                  aria-label={`Extension ${i + 1}`}
                  placeholder="docx"
                  maxLength={17}
                  value={row.extension}
                  onChange={(e) => edit(row.key, { extension: e.target.value })}
                />
              </div>
              <select
                className="input-field"
                aria-label={`Opens in ${i + 1}`}
                value={row.viewer}
                onChange={(e) => edit(row.key, { viewer: e.target.value as DocumentViewer })}
              >
                {DOCUMENT_VIEWERS.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </select>
              <button
                type="button"
                className={styles.removeButton}
                aria-label={`Remove format ${i + 1}`}
                title="Remove"
                onClick={() => remove(row.key)}
                disabled={rows.length === 1}
              >
                <span className="material-symbols-outlined text-lg">delete</span>
              </button>
            </div>
          ))}
        </div>

        <button type="button" className={styles.addButton} onClick={add} disabled={!ready}>
          <span className="material-symbols-outlined text-base">add</span>
          Add format
        </button>
      </div>

      {!ready && loadError && (
        <p className={styles.loadError}>
          Could not load the file formats: {loadError.message}. Saving is disabled until they load — reload the page.
        </p>
      )}

      <div className={styles.saveRow}>
        {error && <span className={styles.errorBadge}>{error}</span>}
        {saved && (
          <span className={styles.savedBadge}>
            <span className="material-symbols-outlined text-sm">check_circle</span>
            Saved
          </span>
        )}
        <button
          className="btn-primary py-2 px-6 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
          onClick={handleSave}
          disabled={saving || !ready}
        >
          {saving ? (
            <>
              <span className="material-symbols-outlined text-sm animate-spin">refresh</span>
              Saving...
            </>
          ) : (
            'Save File Formats'
          )}
        </button>
      </div>
    </div>
  );
};

export default FileFormatsSettings;
